import {
  memo,
  ReactElement,
  Suspense,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EIconSize } from 'utils/constants/app/ui';
import { lazy as lazyimport } from 'utils/generic/lazy';
import {
  ButtonsSectionStyled,
  ButtonWrapperStyled,
  CheckboxSectionStyled,
  CheckboxSectionWrapperStyled,
  ConfirmationPopoverStyled,
  MainSectionIconStyled,
  MainSectionMessageStyled,
  MainSectionStyled,
  MainSectionTitleStyled,
  MainSectionWrapperStyled
} from './ConfirmationPopover.styled';
import { GlobalIcon } from 'components/GlobalIcon';
import { Button } from 'newcomponents/UI/Button';
import { Checkbox } from 'newcomponents/UI/Checkbox';
import { toggleQuickBetPopup } from 'store/actions/betslip';
import { getRecaptchaEnabled } from 'store/selectors/app-data';
import { getShouldShowQuickBetInfoPopover } from 'store/selectors/betslip';

const { Recaptcha } = lazyimport(
  () => import(/* webpackChunkName: "recaptcha" */ 'components/Recaptcha')
);

type TProps = {
  onCancel?: (event: SyntheticEvent) => void | null;
  onConfirm?: (event?: MouseEvent) => void;
  validateRecaptcha?: boolean;
  selectedEvents?: string[];
};

export const ConfirmationPopover = memo(
  ({
    onCancel,
    onConfirm,
    validateRecaptcha,
    selectedEvents
  }: TProps): ReactElement => {
    const dispatch = useDispatch();
    const shouldShowQuickBetInfoPopover = useSelector(
      getShouldShowQuickBetInfoPopover
    );

    const recaptchaEnabled = useSelector(getRecaptchaEnabled);
    const [shouldValidateRecaptchaV3, setShouldValidateRecaptchaV3] =
      useState(false);

    const { t } = useTranslation();

    const onPlaceBet = useCallback(
      (event: SyntheticEvent) => {
        event?.stopPropagation();

        recaptchaEnabled ? setShouldValidateRecaptchaV3(true) : onConfirm?.();
      },
      [recaptchaEnabled]
    );

    useEffect(() => {
      validateRecaptcha && setShouldValidateRecaptchaV3(true);
    }, [validateRecaptcha, selectedEvents]);

    return (
      <>
        {!validateRecaptcha && (
          <ConfirmationPopoverStyled>
            <MainSectionStyled>
              <MainSectionIconStyled>
                <div>
                  <GlobalIcon
                    lib="generic"
                    name="infoOutlined"
                    theme="default"
                    size={EIconSize._30}
                  />
                </div>
              </MainSectionIconStyled>
              <MainSectionWrapperStyled>
                <MainSectionTitleStyled>
                  {t('betslip.howToBet')}
                </MainSectionTitleStyled>
                <MainSectionMessageStyled>
                  {t('betslip.howToBetMessage')}
                </MainSectionMessageStyled>
              </MainSectionWrapperStyled>
            </MainSectionStyled>
            <CheckboxSectionWrapperStyled>
              <CheckboxSectionStyled
                onClick={event => {
                  event.stopPropagation();

                  dispatch(toggleQuickBetPopup());
                }}
              >
                <Checkbox checked={!shouldShowQuickBetInfoPopover}>
                  {t('betslip.dontShowMessage')}
                </Checkbox>
              </CheckboxSectionStyled>
            </CheckboxSectionWrapperStyled>
            <ButtonsSectionStyled>
              <ButtonWrapperStyled>
                <Button
                  fullwidth
                  type="default"
                  size="large"
                  onClick={onCancel}
                >
                  {t('account.cancel')}
                </Button>
              </ButtonWrapperStyled>
              <ButtonWrapperStyled>
                <Button
                  fullwidth
                  type="primary"
                  size="large"
                  onClick={onPlaceBet}
                >
                  {t('betslip.betNow')}
                </Button>
              </ButtonWrapperStyled>
            </ButtonsSectionStyled>
          </ConfirmationPopoverStyled>
        )}
        {recaptchaEnabled && (
          <Suspense>
            <Recaptcha
              action="do_bet"
              onSetRecaptchaToken={() => {
                onConfirm?.();
                setShouldValidateRecaptchaV3(false);
              }}
              shouldValidate={shouldValidateRecaptchaV3}
            />
          </Suspense>
        )}
      </>
    );
  }
);
